import * as T from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { clone } from "three/addons/utils/SkeletonUtils.js";

// Character packs (all CC0, see ASSET_LICENSES.md). Each pack maps its own
// animation names onto the same set of moves, so the player controller and the
// pedestrians work with any of them.
type Move = "idle" | "walk" | "run" | "sprint" | "jumpStart" | "jumpAir" | "jumpLand" | "wave" | "sit" | "dance";
interface Pack {
  label: string;
  characters: Record<string, string>;
  moves: Record<Move, string>;
}
export const PACKS = {
  city: {
    // RG Poly "Cartoon City Massive Pack – Characters" (free version)
    label: "City people · RG Poly",
    characters: {
      Character_2_1_3: "Dev · blue shirt",
      Character_3_2_3: "Mia · purple top",
      Character_1_2_2: "Rob · orange tee",
      Character_4_1_1: "Rose · green dress",
      Character_5_2_3: "Walt · green jacket",
      Character_5_3_1: "Marcus · purple tee",
      Character_6_2_2: "Leo · orange tee",
      Character_8_3_1: "Finn · red tee",
      Character_9_3_4: "Arthur · grey beard",
      Character_9_5_7: "Josh · teal tee",
      Character_10_4_3: "Sam · green tee",
      Character_11_3_1: "Ella · red top",
      Character_B_1: "Bob · builder",
      PoliceMan_A_4_1: "Officer Kay · police",
    },
    moves: { idle: "Idle_A", walk: "Walk_A", run: "Runing_A", sprint: "Runing_B", jumpStart: "Jump_A_Start", jumpAir: "Jump_A_InAir", jumpLand: "Jump_A_Landing", wave: "LookingAround", sit: "Idle_B", dance: "Idle_A_Pose" },
  },
  rpg: {
    // Quaternius Universal Base Characters + Modular Outfits Fantasy + Universal Animation Library
    label: "RPG folk · Quaternius",
    characters: {
      Male_Ranger: "Ranger · man",
      Female_Ranger: "Ranger · woman",
      Male_Peasant: "Villager · man",
      Female_Peasant: "Villager · woman",
    },
    moves: { idle: "Idle_Loop", walk: "Walk_Loop", run: "Jog_Fwd_Loop", sprint: "Sprint_Loop", jumpStart: "Jump_Start", jumpAir: "Jump_Loop", jumpLand: "Jump_Land", wave: "Idle_Talking_Loop", sit: "Sitting_Idle_Loop", dance: "Dance_Loop" },
  },
  adventurers: {
    // KayKit Adventurers Character Pack 1.0
    label: "Adventurers · KayKit",
    characters: { Knight: "Knight", Rogue: "Rogue", Mage: "Mage", Barbarian: "Barbarian", Rogue_Hooded: "Hooded rogue" },
    moves: { idle: "Idle", walk: "Walking_A", run: "Running_A", sprint: "Running_B", jumpStart: "Jump_Start", jumpAir: "Jump_Idle", jumpLand: "Jump_Land", wave: "Cheer", sit: "Sit_Chair_Idle", dance: "Cheer" },
  },
} satisfies Record<string, Pack>;
export type PackId = keyof typeof PACKS;
// Kept for older saved profiles and code that only knows the city people.
export const PEOPLE = PACKS.city.characters;
export type PersonId = keyof typeof PEOPLE;

const loader = new GLTFLoader();
const cache = new Map<string, Promise<{ scene: T.Group; animations: T.AnimationClip[] }>>();
function fetchModel(url: string, onProgress?: (fraction: number) => void) {
  if (!cache.has(url))
    cache.set(
      url,
      loader.loadAsync(url, (e) => {
        if (e.total) onProgress?.(e.loaded / e.total);
      }),
    );
  return cache.get(url)!;
}

/** A posed, animatable character: one or more skinned parts sharing the same moves. */
export interface Rig {
  model: T.Object3D;
  mixers: T.AnimationMixer[];
  actions: Partial<Record<Move, T.AnimationAction[]>>;
}
function prepare(o: T.Object3D) {
  o.traverse((c) => {
    if ((c as T.Mesh).isMesh) {
      c.castShadow = true;
      c.receiveShadow = true;
      c.frustumCulled = false; // skinned bounds don't follow the animation
    }
  });
}
function bindMoves(rig: Rig, part: T.Object3D, clips: T.AnimationClip[], moves: Record<Move, string>) {
  const mixer = new T.AnimationMixer(part);
  rig.mixers.push(mixer);
  for (const [move, name] of Object.entries(moves) as [Move, string][]) {
    const clip = clips.find((c) => c.name === name);
    if (!clip) continue;
    const action = mixer.clipAction(clip);
    if (move === "jumpStart" || move === "jumpLand") {
      action.setLoop(T.LoopOnce, 1);
      action.clampWhenFinished = true;
    }
    (rig.actions[move] ??= []).push(action);
  }
}
// Keep only the head (and neck) of a Quaternius base body: the outfit supplies the rest.
function headOnly(mesh: T.SkinnedMesh) {
  const keep = new Set(mesh.skeleton.bones.map((b, i) => (/^(head|neck)/i.test(b.name) ? i : -1)).filter((i) => i >= 0));
  const g = mesh.geometry, idx = g.getIndex(), si = g.getAttribute("skinIndex"), sw = g.getAttribute("skinWeight");
  if (!idx || !si || !sw) return;
  const dominant = (v: number) => {
    let best = 0, bone = -1;
    for (let k = 0; k < 4; k++) if (sw.getComponent(v, k) > best) (best = sw.getComponent(v, k)), (bone = si.getComponent(v, k));
    return bone;
  };
  const kept: number[] = [];
  for (let i = 0; i < idx.count; i += 3) {
    const a = idx.getX(i), b = idx.getX(i + 1), c = idx.getX(i + 2);
    if (keep.has(dominant(a)) && keep.has(dominant(b)) && keep.has(dominant(c))) kept.push(a, b, c);
  }
  const trimmed = g.clone();
  trimmed.setIndex(kept);
  mesh.geometry = trimmed;
}

export async function loadRig(pack: PackId, id: string, onProgress?: (fraction: number) => void): Promise<Rig> {
  const rig: Rig = { model: new T.Group(), mixers: [], actions: {} };
  const moves = PACKS[pack].moves;
  if (pack === "city") {
    const [gltf, anims] = await Promise.all([fetchModel(`assets/people/${id}.glb`, onProgress), fetchModel("assets/people/Animations.glb")]);
    const part = clone(gltf.scene);
    part.rotation.y = Math.PI; // faces -z in its file
    prepare(part);
    rig.model.add(part);
    bindMoves(rig, part, anims.animations, moves);
  } else if (pack === "adventurers") {
    const gltf = await fetchModel(`assets/characters/${id}.glb`, onProgress);
    const part = clone(gltf.scene);
    part.traverse((o) => {
      if (/sword|shield|knife|crossbow|throwable|spellbook|wand|staff|axe|mug|dagger/i.test(o.name)) o.visible = false;
    });
    const size = new T.Box3().setFromObject(part).getSize(new T.Vector3());
    part.scale.setScalar(1.85 / Math.max(size.y, 0.01));
    prepare(part);
    rig.model.add(part);
    bindMoves(rig, part, gltf.animations, moves);
  } else {
    const female = id.startsWith("Female");
    const hair =
      id === "Male_Peasant" ? ["Hair_SimpleParted", "Hair_Beard"] : id === "Female_Peasant" ? ["Hair_Long"] : id === "Female_Ranger" ? ["Hair_Buns"] : [];
    const base = `assets/quaternius-people/Superhero_${female ? "Female" : "Male"}_FullBody.gltf`;
    const urls = [base, `assets/quaternius-people/${id}.gltf`, ...hair.map((h) => `assets/quaternius-people/${h}.gltf`)];
    let loaded = 0;
    const [anims, ...parts] = await Promise.all([
      fetchModel("assets/quaternius-people/UAL1_Standard.glb"),
      ...urls.map((u) => fetchModel(u).then((g) => (onProgress?.(++loaded / urls.length), g))),
    ]);
    parts.forEach((gltf, i) => {
      const part = clone(gltf.scene);
      if (i === 0) part.traverse((o) => /superhero/i.test(o.name) && (o as T.SkinnedMesh).isSkinnedMesh && headOnly(o as T.SkinnedMesh));
      prepare(part);
      rig.model.add(part);
      bindMoves(rig, part, anims.animations, moves);
    });
  }
  return rig;
}

function mover(rig: Rig) {
  let current: Move | null = null;
  return (move: Move, fade = 0.2, timeScale = 1) => {
    const next = rig.actions[move];
    if (!next) return false;
    for (const a of next) a.timeScale = timeScale;
    if (move === current) return true;
    for (const a of next) a.reset().setEffectiveWeight(1).fadeIn(fade).play();
    if (current) for (const a of rig.actions[current] ?? []) a.fadeOut(fade);
    current = move;
    return true;
  };
}

export function character() {
  const root = new T.Group();
  let rig: Rig | null = null;
  let play: ReturnType<typeof mover> | null = null;
  let previousJump = 0,
    landingUntil = 0,
    previousTime = 0,
    state = "";

  async function use(pack: PackId, id: string, onProgress?: (fraction: number) => void) {
    const next = await loadRig(pack, id, onProgress);
    if (rig) root.remove(rig.model);
    rig = next;
    root.add(rig.model);
    play = mover(rig);
    state = "";
    play("idle", 0);
  }

  return {
    root,
    use,
    /** speed in m/s, jump = height above ground, wave = greeting, dancing = emote */
    update(time: number, speed: number, jump: number, wave: boolean, sitting = false, dancing = false) {
      const dt = Math.min(Math.max(time - previousTime, 0), 0.1);
      previousTime = time;
      if (!rig || !play) return;
      const airborne = jump > 0.01;
      const start = rig.actions.jumpStart?.[0];
      if (airborne && previousJump <= 0.01) {
        play("jumpStart", 0.08);
        state = "start";
      } else if (airborne && state === "start" && (!start || start.time > start.getClip().duration * 0.7)) {
        play("jumpAir", 0.15);
        state = "air";
      } else if (!airborne && previousJump > 0.01) {
        play("jumpLand", 0.08);
        landingUntil = time + 0.3;
        state = "land";
      } else if (!airborne && time > landingUntil) {
        state = "ground";
        if (sitting) play("sit", 0.3);
        else if (dancing && play("dance", 0.2)) {
          // Dance while walking or running; don't wait until speed hits zero.
        } else if (speed > 6.5 && play("sprint", 0.2, speed / 8)) {
          // sprinting
        } else if (speed > 3.5) play("run", 0.2, speed / 5.5);
        else if (speed > 0.2) play("walk", 0.2, Math.max(0.7, speed / 1.6));
        else if (wave) play("wave", 0.25);
        else play("idle", 0.3);
      }
      previousJump = jump;
      for (const m of rig.mixers) m.update(dt);
    },
  };
}

// Pedestrians walking loops round city blocks (local scenery, not players).
export type Loop = { cx: number; cz: number; half: number };
export function crowd(scene: T.Scene, loops: Loop[], count: number, seed = 5) {
  let s = seed;
  const rand = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  const ids = Object.keys(PACKS.city.characters);
  const walkers: { root: T.Group; rig: Rig; loop: Loop; t: number; dir: number; speed: number }[] = [];
  const plans = Array.from({ length: count }, () => ({
    loop: loops[Math.floor(rand() * loops.length)],
    id: ids[Math.floor(rand() * ids.length)],
    t: rand(),
    dir: rand() < 0.5 ? 1 : -1,
    speed: 1.2 + rand() * 0.5,
    phase: rand() * 2,
  }));
  const ready = Promise.all(
    plans.map(async (plan) => {
      const rig = await loadRig("city", plan.id);
      const root = new T.Group();
      root.add(rig.model);
      scene.add(root);
      for (const a of rig.actions.walk ?? []) {
        a.timeScale = plan.speed / 1.4;
        a.play();
      }
      for (const m of rig.mixers) m.update(plan.phase);
      walkers.push({ root, rig, loop: plan.loop, t: plan.t, dir: plan.dir, speed: plan.speed });
    }),
  );
  // Position on the square loop (perimeter parameter, wraps every 1) and heading.
  const at = (loop: Loop, t: number) => {
    const u = (((t % 1) + 1) % 1) * 4, side = Math.floor(u), f = u - side, h = loop.half;
    const pts = [[-h, h], [h, h], [h, -h], [-h, -h]];
    const [ax, az] = pts[side], [bx, bz] = pts[(side + 1) % 4];
    return { x: loop.cx + ax + (bx - ax) * f, z: loop.cz + az + (bz - az) * f, heading: Math.atan2(bx - ax, bz - az) };
  };
  return {
    ready,
    update(dt: number, eye: T.Vector3) {
      for (const w of walkers) {
        w.t += (w.dir * w.speed * dt) / (w.loop.half * 8);
        const p = at(w.loop, w.t);
        w.root.position.set(p.x, 0, p.z);
        w.root.rotation.y = p.heading + (w.dir < 0 ? Math.PI : 0);
        const near = Math.hypot(p.x - eye.x, p.z - eye.z) < 110;
        w.root.visible = near;
        if (near) for (const m of w.rig.mixers) m.update(dt);
      }
    },
  };
}
